<script setup lang="ts">
    import type { TrainingCourseDetail } from '~/types/academy/baseTraining';

    const props = defineProps<{
        training: TrainingCourseDetail;
    }>();

    const isPlayerOpen = ref(false);

    const refreshAcademyCourseData = useAcademyCourseRefresh();

    /** Only marks the cached course stale: the card is brought up to date when the player closes. */
    function onCourseStatusChanged() {
        invalidateAcademyCourseCache(props.training.courseId);
    }

    function onPlayerClose() {
        isPlayerOpen.value = false;
        refreshAcademyCourseData(props.training.courseId);
    }

    const isCompleted = computed(() => props.training.status === 'completed');

    const cardRef = ref<HTMLElement | null>(null);

    onMounted(() => {
        if (!cardRef.value) return;
        let lastHeight = 0;
        let lastWidth = 0;
        const update = () => {
            if (!cardRef.value) return;
            const h = cardRef.value.offsetHeight;
            const w = cardRef.value.offsetWidth;
            if (h !== lastHeight) {
                lastHeight = h;
                cardRef.value.style.setProperty('--card-height', `${h}px`);
            }
            if (w !== lastWidth) {
                lastWidth = w;
                document.documentElement.style.setProperty('--cta-card-width', `${w}px`);
            }
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(cardRef.value);
        onUnmounted(() => {
            observer.disconnect();
            document.documentElement.style.removeProperty('--cta-card-width');
        });
    });

    const { hfpPushError } = useNotificationHelpers();
    const { t } = useI18n();

    function extractFilename(contentDisposition: string | null): string {
        const match = contentDisposition?.match(/filename="([^"]+)"/);
        return match?.[1] ?? `certificate-${props.training.courseId}.pdf`;
    }

    const isDownloading = ref(false);

    async function downloadCertificate() {
        if (isDownloading.value) return;
        isDownloading.value = true;
        try {
            const response = await $fetch.raw(`/api/academy/docebo/certificate/${props.training.courseId}`, {
                responseType: 'blob'
            });

            const url = URL.createObjectURL(response._data as Blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = extractFilename(response.headers.get('content-disposition'));
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            hfpPushError(t('messages.error'));
        } finally {
            isDownloading.value = false;
        }
    }
</script>

<template>
    <aside
        ref="cardRef"
        class="academy-training-cta-card"
    >
        <div
            v-if="training.image"
            class="card-image-wrapper"
            :class="{ 'card-image-wrapper--completed': isCompleted }"
        >
            <img
                :src="training.image.src"
                :alt="training.image.alt ?? training.title"
                class="card-image"
            />
            <TeaserPoints
                v-if="training.points !== null"
                :points="training.points"
                class="training-points"
            />
        </div>

        <div class="card-header">
            <h2>{{ training.title }}</h2>
            <TeaserPoints
                v-if="training.points !== null && !training.image"
                :points="training.points"
                class="training-points"
            />
        </div>

        <AcademyTrainingProgress :status="training.status" />

        <TeaserDetails :item="training" />

        <button
            v-if="training.certificateUrl"
            type="button"
            class="certificate-download-button"
            :disabled="isDownloading"
            @click="downloadCertificate"
        >
            {{ $t('academy.training.cta.downloadCertificate') }}
            <SimpleLoadingSpinner
                v-if="isDownloading"
                :size="18"
            />
            <Icon
                v-else
                class="certificate-download-icon"
                name="lucide:download"
                mode="svg"
                size="18px"
            />
        </button>

        <button
            type="button"
            class="teaser-play-button"
            :class="{ 'teaser-play-button--outline': isCompleted }"
            :aria-label="$t('academy.training.cta.startAriaLabel')"
            @click="isPlayerOpen = true"
        >
            {{ isCompleted ? $t('academy.training.cta.repeat') : $t('academy.training.cta.start') }}
            <Icon
                class="teaser-play-icon"
                name="hfp:circle-play"
                mode="svg"
                size="18px"
            />
        </button>

        <BasicModal
            :is-open="isPlayerOpen"
            :close-on-click-outside="false"
            full-screen
            request-orientation="landscape"
            :aria-label="$t('academy.training.player.ariaLabel')"
            @close="onPlayerClose"
        >
            <template #content>
                <div class="player-modal-content">
                    <DoceboCoursePlayer
                        v-if="isPlayerOpen"
                        id="academy-training-player"
                        :course-id="training.courseId"
                        @course-status-changed="onCourseStatusChanged"
                    />
                </div>
            </template>
        </BasicModal>
    </aside>
</template>

<style scoped lang="scss">
    .academy-training-cta-card {
        position: relative;
        top: calc(var(--spacing--4) * -1);
        margin-bottom: calc(var(--spacing--4) * -1);
        align-self: start;
        border-radius: var(--border-radius-s);
        display: flex;
        flex-direction: column;
        gap: var(--spacing);
        padding: var(--spacing);
        width: 100%;
        box-shadow: 0 4px 10px color-mix(in srgb, var(--black) 25%, transparent);
        background: var(--white);

        @media (min-width: $br-m) {
            top: calc((var(--card-height, 0px) / -2) - var(--spacing--4));
            margin-bottom: calc((var(--card-height, 0px) / -2) - var(--spacing--4));
        }

        .teaser-details {
            background-color: #e6f1f6;
            border-radius: var(--border-radius-s);
            padding: var(--spacing);

            :deep(.teaser-icon) {
                background-color: transparent;
                width: 24px;
                height: 24px;
            }
        }

        .card-image-wrapper {
            position: relative;
        }

        .card-image {
            width: 100%;
            border-radius: var(--border-radius-s);
            display: block;
        }

        .card-image-wrapper--completed .card-image {
            opacity: 0.5;
        }

        .card-image-wrapper .training-points {
            position: absolute;
            right: var(--spacing);
            bottom: 0;
            transform: translateY(50%);
            width: 66px;
            height: 66px;
            font-size: 22px;
        }

        .card-header .training-points {
            position: absolute;
            right: var(--spacing);
            top: 0;
            transform: translateY(-50%);
            width: 66px;
            height: 66px;
            font-size: 22px;
        }
    }

    .card-header {
        align-items: start;
    }

    .academy-training-cta-card:has(.training-points) .card-header {
        padding-right: calc(66px + var(--spacing));
    }

    h2 {
        color: var(--black);
        font-size: var(--font-m);
        font-weight: 700;
        line-height: 1.18;
        letter-spacing: -0.01em;
        margin: 0;
        overflow-wrap: break-word;
        min-width: 0;
    }

    .certificate-download-button,
    .teaser-play-button {
        align-items: center;
        aspect-ratio: 1/1;
        background: var(--brand-primary);
        border: 0;
        border-radius: var(--border-radius-s);
        color: #ffffff;
        cursor: pointer;
        display: inline-flex;
        height: 2.5rem;
        justify-content: space-between;
        padding: 0;
        width: 100%;
        padding-inline: var(--spacing);

        span {
            height: 50%;
        }

        :deep(.teaser-play-icon),
        :deep(.teaser-play-icon *),
        :deep(.certificate-download-icon),
        :deep(.certificate-download-icon *) {
            stroke-width: 2;
        }
    }

    .certificate-download-button:disabled {
        opacity: 0.5;
        cursor: wait;
    }

    .teaser-play-button--outline {
        background: var(--white);
        border: 1px solid var(--black);
        color: var(--black);

        :deep(.teaser-play-icon),
        :deep(.teaser-play-icon *) {
            color: var(--black);
        }
    }

    .player-modal-content {
        height: 100%;
        min-height: 0;
    }

    .player-modal-content :deep(dcbo-course-player) {
        display: block;
        height: 100%;
        width: 100%;
    }
</style>
